<template>
    <section id="study" class="study-card card mb-3">
        <form
            v-if="study"
            ref="studiesForm"
            class="card-body"
            v-click-outside="onClickOutside"
            @click="focusBlock"
        >
            <div class="card-heading">
                <h5 class="card-name">
                    {{ $t('lib_education') }}
                </h5>
            </div>
            <hr />
            <p
                class="block-error-feedback text-danger"
                v-if="blockErrorMessage(block.name)"
            >
                <font-awesome-icon icon="fas fa-exclamation-circle" />
                {{ $t(blockErrorMessage(block.name)) }}
            </p>
            <p
                class="block-error-feedback text-danger"
                v-if="blockErrorMessage(block.name, 'studies')"
            >
                <font-awesome-icon icon="fas fa-exclamation-circle" />
                {{ $t(blockErrorMessage(block.name, 'studies')) }}
            </p>
            <div
                v-for="(studyObj, index) in study.studies"
                :key="index"
                class="row form-row mb-3"
            >
                <div
                    class="form-group col-sm-6"
                    :class="{ 'required-group': isFieldRequired('schoolName') }"
                >
                    <label for="schoolName" class="form-label">
                        {{ $t('lib_org_name') }}
                    </label>
                    <input
                        type="text"
                        v-model="studyObj.schoolName"
                        class="form-control"
                        :class="{
                            'is-invalid': errorMessage(
                                'schoolName',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        id="schoolName"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'schoolName',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'schoolName',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-3"
                    :class="{
                        'required-group': isFieldRequired('studyCountryCode'),
                    }"
                >
                    <label for="country" class="form-label">
                        {{ $t('lib_country') }}
                    </label>
                    <Dropdown
                        appendTo="self"
                        :filter="true"
                        :showClear="true"
                        inputId="country"
                        v-model="studyObj.studyCountryCode"
                        :class="{
                            'is-invalid': errorMessage(
                                'studyCountryCode',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        class="w-100"
                        :options="countries"
                        optionLabel="name"
                        optionValue="code"
                        :placeholder="$t('cand_choose')"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'studyCountryCode',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'studyCountryCode',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-6"
                    :class="{ 'required-group': isFieldRequired('diploma') }"
                >
                    <label for="diploma" class="form-label">
                        {{ $t('lib_diploma') }}
                    </label>
                    <input
                        type="text"
                        v-model="studyObj.diploma"
                        class="form-control"
                        :class="{
                            'is-invalid': errorMessage(
                                'diploma',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        id="diploma"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'diploma',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'diploma',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-3"
                    :class="{ 'required-group': isFieldRequired('studyLevel') }"
                >
                    <label for="studyLevel" class="form-label">
                        {{ $t('lib_edu_level') }}
                    </label>
                    <Dropdown
                        inputId="studyLevel"
                        v-model="studyObj.studyLevel"
                        :class="{
                            'is-invalid': errorMessage(
                                'studyLevel',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        class="w-100"
                        :options="filteredStudyLevels"
                        optionLabel="name"
                        optionValue="code"
                        appendTo="self"
                        :placeholder="$t('cand_choose')"
                        :required="!applicationTypeIsShort"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'studyLevel',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'studyLevel',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                    <p class="small text-muted">
                        <em
                            ><a
                                href="https://education.ec.europa.eu/study-in-europe/planning-studies/higher-education-europe"
                                target="_blank"
                                aria-label="Higher education in Europe (europa.eu)"
                            >
                                Higher education in Europe (europa.eu)
                            </a>
                        </em>
                    </p>
                </div>
                <div
                    class="form-group col-sm-3"
                    :class="{ 'required-group': isFieldRequired('studyType') }"
                >
                    <label for="studyType" class="form-label">
                        {{ $t('lib_edu_field') }}
                    </label>
                    <Dropdown
                        inputId="studyType"
                        v-model="studyObj.studyType"
                        :class="{
                            'is-invalid': errorMessage(
                                'studyType',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        class="w-100"
                        :options="studyFields"
                        appendTo="self"
                        optionLabel="name"
                        optionValue="code"
                        :placeholder="$t('cand_choose')"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'studyType',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'studyType',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-3"
                    :class="{
                        'required-group': isFieldRequired('studyStartDate'),
                    }"
                >
                    <label for="studyStartDate" class="form-label">
                        {{ $t('lib_from') }}
                    </label>
                    <Calendar
                        v-model="studyObj.studyStartDate"
                        :showIcon="true"
                        appendTo="self"
                        :minDate="minStartDateValue"
                        :class="{
                            'is-invalid': errorMessage(
                                'studyStartDate',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        class="w-100"
                        id="studyStartDate"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'studyStartDate',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'studyStartDate',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-3"
                    :class="{
                        'required-group': isFieldRequired('studyEndDate'),
                    }"
                >
                    <label for="studyEndDate" class="form-label">
                        {{ $t('lib_to') }}
                    </label>
                    <Calendar
                        v-model="studyObj.studyEndDate"
                        :showIcon="true"
                        :minDate="minStudyEndDate(studyObj.studyStartDate)"
                        appendTo="self"
                        :class="{
                            'is-invalid': errorMessage(
                                'studyEndDate',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        class="w-100"
                        id="studyEndDate"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'studyEndDate',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'studyEndDate',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div
                    class="form-group col-sm-3"
                    :class="{
                        'required-group': isFieldRequired('graduationDate'),
                    }"
                >
                    <label for="graduationDate" class="form-label">
                        {{ $t('lib_edu_date') }}
                    </label>
                    <Calendar
                        v-model="studyObj.graduationDate"
                        :showIcon="true"
                        :minDate="studyObj.studyEndDate"
                        appendTo="self"
                        :class="{
                            'is-invalid': errorMessage(
                                'graduationDate',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                        class="w-100"
                        id="graduationDate"
                        @dateSelect="graduationDateChange($event, index)"
                    />
                    <div
                        v-if="
                            errorMessage(
                                'graduationDate',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'graduationDate',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div class="form-group col-sm-12">
                    <label for="comment" class="form-label">
                        {{ $t('lib_comments') }}
                    </label>
                    <textarea
                        id="comment"
                        type="text"
                        maxlength="600"
                        v-model="studyObj.comment"
                        class="form-control"
                        :class="{
                            'is-invalid': errorMessage(
                                'comment',
                                block.name,
                                'studies',
                                index
                            ),
                        }"
                    />
                    <p class="textarea-character-length">
                        {{
                            studyObj.comment ? studyObj.comment.length : '0'
                        }}/600
                    </p>
                    <div
                        v-if="
                            errorMessage(
                                'comment',
                                block.name,
                                'studies',
                                index
                            )
                        "
                        class="invalid-feedback"
                    >
                        {{
                            $t(
                                errorMessage(
                                    'comment',
                                    block.name,
                                    'studies',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div class="d-flex justify-content-center">
                    <button
                        type="button"
                        v-show="study.studies.length > study.minItems"
                        @click="removeStudy(index)"
                        class="btn-remove"
                        v-tooltip.top.hover.focus="$t('lib_remove_study')"
                    >
                        <font-awesome-icon icon="fas fa-trash"/>
                    </button>
                </div>
                
                <div class="col-sm-12 card-divider mt-2">
                    <hr />
                </div>
            </div>
            <div
                class="add-study-btn btn-add-wrapper"
                @click="addStudy"
                v-show="study.studies.length < study.maxItems"
            >
                <button type="button" class="btn-add">
                    <font-awesome-icon icon="fas fa-plus"/>
                </button>
                {{ $t('lib_add') }}
            </div>
            <div class="card-footer d-flex justify-content-end mt-3" v-if="formWasChanged">
                <div class="card-actions">
                    <button
                        type="button"
                        class="card-action save-action btn btn-text me-2"
                        @click="validate()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="far fa-save"  class="fs-5 btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_save') }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="card-action undo-changes-action btn btn-text"
                        @click="undoChanges()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="fas fa-undo" class="btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import _ from 'lodash';

export default {
    name: 'Study',
    components: {
        Calendar,
        Dropdown,
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            study: null,
            studyModel: {
                diploma: '',
                graduationDate: null,
                studyStartDate: null,
                studyEndDate: null,
                id: 0,
                isThisTraineeshipRequired: false,
                schoolName: '',
                studyCountryCode: '',
                studyLevel: '',
                studyType: '',
            },
            listenToClickOutside: false,
            formWasChanged: false,
            filteredStudyLevels: null
        };
    },
    computed: {
        ...mapGetters([
            'studyFields',
            'studyLevels',
            'wizardErrors',
            'blockErrorMessage',
            'errorMessage',
            'isFieldRequired',
            'studyData',
            'countries',
            'applicationTypeIsTrainee',
            'minStartDateValue',
            'appLoading',
            'applicationTypeIsShort',
            'isCORSite'
        ]),
    },
    methods: {
        ...mapActions([
            'validateBlock',
            'updateBlock',
            'clearBlockErrors',
            'focusOnBlock',
        ]),
        validate() {
            this.clearBlockErrors(this.block.name);
            this.validateBlock({
                blockData: { ...this.study },
                blockName: this.block.name,
            }).then(
                () => {
                    // success
                    this.updateBlock({
                        blockName: this.block.name,
                        application: {
                            applicationId: this.applicationId,
                            applicationType: this.applicationType,
                            applicationBlocks: {
                                [this.block.name]: this.study,
                            },
                        },
                    }).then(
                        () => {
                            // success
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'success',
                                detail: this.$t('mess_save_success'),
                                life: 3000,
                            });
                            this.loadData();
                            this.$emit('checkErrorForOtherBlocks');
                        },
                        () => {
                            // error
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'error',
                                detail: this.$t('mess_save_failed'),
                                life: 3000,
                            });
                        }
                    );
                },
                () => {
                    // error
                    this.listenToClickOutside = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('mess_save_failed'),
                        life: 3000,
                    });
                }
            );
        },
        addStudy() {
            let tempData = [...this.study.studies];
            tempData.push(_.cloneDeep(this.studyModel));
            this.study.studies = tempData;
        },
        removeStudy(index) {
            let tempData = [...this.study.studies];
            tempData.splice(index, 1);
            this.study.studies = tempData;
        },
        onClickOutside() {
            if (
                this.listenToClickOutside &&
                this.formWasChanged &&
                !this.appLoading
            ) {
                this.clearBlockErrors(this.block.name);
                this.validate();
            }
            this.focusOnBlock(null);
        },
        undoChanges() {
            this.loadData();
            this.clearBlockErrors(this.block.name);
        },
        loadData() {
            this.study = _.cloneDeep(this.studyData);
            this.study.studies = this.formatDates(this.study.studies);
        },
        filterStudyLevels() {
            this.filteredStudyLevels = this.applicationTypeIsShort ? this.studyLevels.filter((item) => { return (item.group != "SECONDARY") }) : this.studyLevels;
        },
        focusBlock() {
            this.listenToClickOutside = true;
            this.focusOnBlock({
                name: 'study',
                label: this.$t('lib_education'),
            });
        },
        minStudyEndDate(startDate) {
            const minDate = new Date(startDate);
            minDate.setDate(minDate.getDate() + 1);
            return minDate;
        },
        canShowTraineeshipRequired(studyObj) {
            const today = new Date().setHours(0, 0, 0, 0);

            if (this.isCORSite){
                return (!this.applicationTypeIsTrainee && studyObj.graduationDate > today)
            } else {
                return (!this.applicationTypeIsTrainee)
            }
        },
        graduationDateChange(event, studyIndex) {
            const today = new Date().setHours(0, 0, 0, 0);
            if (this.study.studies[studyIndex].graduationDate <= today) {
                this.study.studies[
                    studyIndex
                ].isThisTraineeshipRequired = false;
            }
        },
        formatDates(studies) {
            return studies.map((element) => {
                element.graduationDate = element.graduationDate
                    ? new Date(element.graduationDate)
                    : element.graduationDate;
                element.studyStartDate = element.studyStartDate
                    ? new Date(element.studyStartDate)
                    : element.studyStartDate;
                element.studyEndDate = element.studyEndDate
                    ? new Date(element.studyEndDate)
                    : element.studyEndDate;
                return element;
            });
        },
    },
    mounted() {
        this.loadData();
        this.filterStudyLevels();
    },
    watch: {
        // whenever study object changes, this function will run and this way we will now if the form data was changed so we can save changes;
        study: {
            handler(newData, oldData) {
                const storeData = {
                    ...this.studyData,
                    studies: this.formatDates(this.studyData.studies),
                };
                this.formWasChanged =
                    JSON.stringify(newData) !== JSON.stringify(storeData);

                if (!this.formWasChanged && oldData) {
                    // checking for oldData to prevent clearing the errors on firstload;
                    this.clearBlockErrors(this.block.name);
                }
            },
            deep: true,
        },
    },
};
</script>
